// Formatting and sizing utilities for playground metrics.
// Example: formatBytes(2048) -> "2.00 KB".

// Format milliseconds with adaptive precision.
export function formatDuration(durationMs: number): string {
  // Guard against invalid or missing timing values.
  if (!Number.isFinite(durationMs)) {
    return '0 ms'
  } else if (durationMs < 1) {
    // Use higher precision for sub-millisecond measurements.
    return `${durationMs.toFixed(3)} ms`
  } else if (durationMs < 10) {
    // Use medium precision for short durations under 10 ms.
    return `${durationMs.toFixed(2)} ms`
  } else {
    // Return compact duration for longer timings.
    return `${durationMs.toFixed(1)} ms`
  }
}

// Derive line count from source text while keeping one visible empty line.
export function getLineCount(content: string): number {
  // Keep at least one line marker when content is empty.
  if (!content) {
    return 1
  } else {
    // Return newline-based line count for non-empty content.
    return content.split(/\r?\n/).length
  }
}

// Estimate UTF-8 byte length for browser memory footprint display.
export function getUtf8ByteLength(content: string): number {
  // Return UTF-8 byte size for the input string.
  return new TextEncoder().encode(content).length
}

// Format byte counts into readable units for the playground footer.
export function formatBytes(bytes: number): string {
  const units = ['B', 'KB', 'MB']
  let value = bytes
  let unitIndex = 0
  // Step through units while value is large enough for next unit.
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024
    unitIndex += 1
  }

  const precision = unitIndex === 0 ? 0 : 2
  // Return human-readable byte string.
  return `${value.toFixed(precision)} ${units[unitIndex]}`
}
